import { useState, useCallback, useMemo, useEffect } from "react";
import { Toaster } from "sonner";
import { useAuth, AuthProvider } from "./hooks/useAuth";
import { useProfile, ProfileProvider } from "./hooks/useProfile";
import { useDecisions, DecisionsProvider, type Decision } from "./hooks/useDecisions";
import type { Page, StoredUserSettings } from "./types";
import { ThemeProvider, useTheme } from "../components/ThemeProvider";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "../components/ui/sidebar";
import { LandingPage } from "../components/LandingPage";
import { SignIn } from "../components/SignIn";
import { SignUp } from "../components/SignUp";
import { ForgotPassword } from "../components/ForgotPassword";
import { ResetPassword } from "../components/ResetPassword";
import { DashboardSidebar } from "../components/DashboardSidebar";
import { DashboardHeader } from "../components/DashboardHeader";
import { DashboardContent } from "../components/DashboardContent";
import { NewDecisionForm } from "../components/NewDecisionForm";
import { MyDecisions } from "../components/MyDecisions";
import { DecisionDetail } from "../components/DecisionDetail";
import { Analytics } from "../components/Analytics";
import { Reviews } from "../components/Reviews";
import { Log } from "../components/Log";
import { Settings } from "../components/Settings";
import { Help } from "../components/Help";
import { Pricing } from "../components/Pricing";
import { Blog } from "../components/Blog";
import { HelpCenter } from "../components/HelpCenter";
import { BestPractices } from "../components/BestPractices";
import { AppBlog } from "../components/AppBlog";
import { AppBestPractices } from "../components/AppBestPractices";
import { About } from "../components/About";
import { Privacy } from "../components/Privacy";
import { Terms } from "../components/Terms";
import { Contact } from "../components/Contact";
import { ToastProvider } from "../components/toast/ToastProvider";
import { PaymentHandler } from "../components/PaymentHandler";
import { ReleaseNotesPopup } from "../components/ReleaseNotesPopup";
import { CommandPalette } from "../components/CommandPalette";
import { peekPendingUpgrade } from "./lib/pendingUpgrade";

function AppContent() {
  const { user, loading, signOut } = useAuth();
  const { profile } = useProfile();
  const {
    decisions,
    addRating,
    getCurrentRating,
    detailsByDecisionId,
    staleDetails,
    openDecisionDetails,
  } = useDecisions();
  const isAuthenticated = !!user;
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [hasShownReleaseNotes, setHasShownReleaseNotes] = useState(false);
  
  // Initialize page based on URL path for direct access pages
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    const path = window.location.pathname;
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    if (path.startsWith('/decision/')) return 'decision-detail';
    return "landing";
  });
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(() => {
    const path = window.location.pathname;
    if (path.startsWith('/decision/')) {
      return path.split('/')[2] || null;
    }
    return null;
  });
  const [selectedDecisionTag, setSelectedDecisionTag] = useState<string | null>(null);
  const [analyticsDefaultTab, setAnalyticsDefaultTab] = useState<"trends" | "distribution" | "insights">("trends");

  // Handle URL changes and authentication state changes
  useEffect(() => {
    const path = window.location.pathname;
    
    // Handle URL-based routing for direct-access pages
    if (path === '/privacy' && currentPage !== 'privacy') {
      setCurrentPage('privacy');
      return;
    }
    if (path === '/terms' && currentPage !== 'terms') {
      setCurrentPage('terms');
      return;
    }
    
    // Handle decision URLs
    if (path.startsWith('/decision/') && !loading) {
      const decisionId = path.split('/')[2];
      if (decisionId && isAuthenticated) {
        setSelectedDecisionId(decisionId);
        setCurrentPage('decision-detail');
        return;
      } else if (!isAuthenticated) {
        console.log('Unauthenticated user accessing decision, redirecting to landing');
        setCurrentPage("landing");
        return;
      }
    }
    
    // For root path, handle authentication-based routing
    if (path === '/' && !loading) {
      if (isAuthenticated && currentPage === "landing") {
        // A plan picked before signing up sends the new user to pricing rather
        // than the dashboard, where Pricing resumes the checkout. Without this
        // the click that started the sign-up is lost and they arrive on the
        // free plan with nothing to show they asked to upgrade.
        if (peekPendingUpgrade()) {
          setCurrentPage("pricing");
        } else {
          console.log('Authenticated user detected, navigating to dashboard');
          setCurrentPage("dashboard");
        }
      } else if (!isAuthenticated && !["landing", "sign-in", "sign-up", "forgot-password", "reset-password", "blog", "help-center", "best-practices", "about", "privacy", "terms", "contact"].includes(currentPage)) {
        console.log('Unauthenticated user on protected page, redirecting to landing');
        setCurrentPage("landing");
      }
    }
  }, [user, loading, isAuthenticated, currentPage]);

  // Listen for popstate events (back/forward browser navigation)
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/privacy') setCurrentPage('privacy');
      else if (path === '/terms') setCurrentPage('terms');
      else if (path.startsWith('/decision/')) {
        const decisionId = path.split('/')[2];
        if (decisionId && isAuthenticated) {
          setSelectedDecisionId(decisionId);
          setCurrentPage('decision-detail');
        }
      }
      else if (path === '/') setCurrentPage('landing');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // isAuthenticated is read inside the handler: without it here, a back
    // navigation to /decision/:id after signing in still sees the signed-out
    // value captured at mount and does nothing.
  }, [isAuthenticated]);

  // Show release notes popup when user logs in (if enabled)
  useEffect(() => {
    if (user && !hasShownReleaseNotes && profile?.notification_preferences) {
      const prefs = profile.notification_preferences as StoredUserSettings;
      const shouldShowPopup = prefs.showReleaseNotesPopup !== false; // Default to true
      
      if (shouldShowPopup) {
        setShowReleaseNotes(true);
        setHasShownReleaseNotes(true);
      }
    }
  }, [user, profile, hasShownReleaseNotes]);

  // Navigation functions
  const navigateToLanding = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("landing");
  }, []);
  const navigateToSignIn = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("sign-in");
  }, []);
  const navigateToSignUp = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("sign-up");
  }, []);
  const navigateToForgotPassword = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("forgot-password");
  }, []);
  const navigateToDashboard = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("dashboard");
  }, []);
  const navigateToNewDecision = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("new-decision");
  }, []);
  const navigateToMyDecisions = useCallback(() => {
    window.history.pushState({}, '', '/');
    setSelectedDecisionTag(null);
    setCurrentPage("my-decisions");
  }, []);
  const navigateToAnalytics = useCallback(() => {
    window.history.pushState({}, '', '/');
    setAnalyticsDefaultTab("trends");
    setCurrentPage("analytics");
  }, []);
  const navigateToAnalyticsInsights = useCallback(() => {
    window.history.pushState({}, '', '/');
    setAnalyticsDefaultTab("insights");
    setCurrentPage("analytics");
  }, []);
  const navigateToReviews = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("reviews");
  }, []);
  const navigateToLog = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("log");
  }, []);
  const navigateToSettings = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("settings");
  }, []);
  const navigateToHelp = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("help");
  }, []);
  const navigateToPricing = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("pricing");
  }, []);

  // In-app resource navigation
  const navigateToAppBlog = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("app-blog");
  }, []);
  const navigateToAppBestPractices = useCallback(() => {
    window.history.pushState({}, '', '/');
    setCurrentPage("app-best-practices");
  }, []);

  // Footer navigation
  const navigateToBlog = useCallback(() => setCurrentPage("blog"), []);
  const navigateToHelpCenter = useCallback(() => setCurrentPage("help-center"), []);
  const navigateToBestPractices = useCallback(() => setCurrentPage("best-practices"), []);
  const navigateToAbout = useCallback(() => setCurrentPage("about"), []);
  const navigateToPrivacy = useCallback(() => {
    window.history.pushState({}, '', '/privacy');
    setCurrentPage("privacy");
  }, []);
  const navigateToTerms = useCallback(() => {
    window.history.pushState({}, '', '/terms');
    setCurrentPage("terms");
  }, []);
  const navigateToContact = useCallback(() => setCurrentPage("contact"), []);

  const navigateToDecisionDetail = useCallback((decisionId: string) => {
    window.history.pushState({}, '', `/decision/${decisionId}`);
    setSelectedDecisionId(decisionId);
    setCurrentPage("decision-detail");
  }, []);

  // Authentication handlers
  const handleSignIn = useCallback(() => {
    navigateToDashboard();
  }, [navigateToDashboard]);

  const handleSignUp = useCallback(() => {
    navigateToSignIn();
  }, [navigateToSignIn]);

  const handlePasswordReset = useCallback(() => {
    navigateToSignIn();
  }, [navigateToSignIn]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigateToLanding();
  }, [navigateToLanding, signOut]);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  // The summary list carries per-status review counts, not review rows, so the
  // detail view needs the decision's own scheduled_reviews and rating_entries.
  // Render the summary meanwhile, and refetch whenever a write marks the cached
  // detail stale.
  const detailIsStale = selectedDecisionId ? !!staleDetails[selectedDecisionId] : false;
  const hasDetail = selectedDecisionId ? !!detailsByDecisionId[selectedDecisionId] : false;

  useEffect(() => {
    if (currentPage !== "decision-detail" || !selectedDecisionId) return;
    if (hasDetail && !detailIsStale) return;

    // Force: the freshness check above reads state, which the hook's refs can
    // lag behind (a child effect runs before the provider's ref sync).
    openDecisionDetails(selectedDecisionId, true).catch((error) => {
      console.error("Failed to load decision details:", error);
    });
  }, [currentPage, selectedDecisionId, hasDetail, detailIsStale, openDecisionDetails]);

  const selectedDecision = useMemo(() => {
    if (!selectedDecisionId) return null;
    return (
      detailsByDecisionId[selectedDecisionId] ||
      decisions.find(d => d.id === selectedDecisionId) ||
      null
    );
  }, [selectedDecisionId, detailsByDecisionId, decisions]);

  // Memoized landing page props
  const landingPageProps = useMemo(() => ({
    onSignIn: navigateToSignIn,
    onSignUp: navigateToSignUp,
    onScrollToSection: scrollToSection,
    onNavigateToBlog: navigateToBlog,
    onNavigateToHelpCenter: navigateToHelpCenter,
    onNavigateToBestPractices: navigateToBestPractices,
    onNavigateToAbout: navigateToAbout,
    onNavigateToPrivacy: navigateToPrivacy,
    onNavigateToTerms: navigateToTerms,
    onNavigateToContact: navigateToContact
  }), [
    navigateToSignIn, navigateToSignUp, scrollToSection,
    navigateToBlog, navigateToHelpCenter, navigateToBestPractices,
    navigateToAbout, navigateToPrivacy, navigateToTerms, navigateToContact
  ]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Render different pages based on current page
  if (currentPage === "landing") {
    return <LandingPage {...landingPageProps} />;
  }

  if (currentPage === "sign-in") {
    return (
      <SignIn 
        onSignIn={handleSignIn}
        onNavigateToSignUp={navigateToSignUp}
        onNavigateToForgotPassword={navigateToForgotPassword}
        onBackToLanding={navigateToLanding}
      />
    );
  }

  if (currentPage === "sign-up") {
    return (
      <SignUp 
        onSignUp={handleSignUp}
        onNavigateToSignIn={navigateToSignIn}
        onBackToLanding={navigateToLanding}
      />
    );
  }

  if (currentPage === "forgot-password") {
    return (
      <ForgotPassword 
        onBackToSignIn={navigateToSignIn}
        onBackToLanding={navigateToLanding}
      />
    );
  }

  if (currentPage === "reset-password") {
    return (
      <ResetPassword 
        onPasswordReset={handlePasswordReset}
        onBackToSignIn={navigateToSignIn}
        onBackToLanding={navigateToLanding}
      />
    );
  }

  // Footer pages - accessible without authentication
  const footerPages = {
    blog: () => <Blog onBackToLanding={navigateToLanding} />,
    "help-center": () => <HelpCenter onBackToLanding={navigateToLanding} />,
    "best-practices": () => <BestPractices onBackToLanding={navigateToLanding} />,
    about: () => <About onBackToLanding={navigateToLanding} />,
    privacy: () => <Privacy onBackToLanding={navigateToLanding} />,
    terms: () => <Terms onBackToLanding={navigateToLanding} />,
    contact: () => <Contact onBackToLanding={navigateToLanding} />
  };

  if (currentPage in footerPages) {
    const PageComponent = footerPages[currentPage as keyof typeof footerPages];
    return <PageComponent />;
  }

  // Authentication guard
  if (!isAuthenticated) {
    return <LandingPage {...landingPageProps} />;
  }

  // Authenticated app layout with proper sidebar structure
  return (
    <SidebarProvider>
      <div className="min-h-screen w-full flex relative">
        {/* Sidebar */}
        <DashboardSidebar 
          onLogoClick={navigateToLanding}
          onNavigateHome={navigateToDashboard}
          onNewDecision={navigateToNewDecision}
          onNavigateToMyDecisions={navigateToMyDecisions}
          onNavigateToAnalytics={navigateToAnalytics}
          onNavigateToReviews={navigateToReviews}
          onNavigateToLog={navigateToLog}
          onNavigateToSettings={navigateToSettings}
          onNavigateToHelp={navigateToHelp}
          onNavigateToAppBlog={navigateToAppBlog}
          onNavigateToAppBestPractices={navigateToAppBestPractices}
          currentPage={currentPage}
        />
        
        {/* Main content area */}
        <SidebarInset className="flex-1 overflow-hidden w-full min-w-0">
          {/* Enhanced header with prominent sidebar trigger */}
          <header className="flex h-12 shrink-0 items-center border-b bg-background px-4 sticky top-0 z-40">
            <SidebarTrigger className="mr-2 h-8 w-8 hover:bg-accent hover:text-accent-foreground" />
          </header>
          
          {/* Dashboard header section */}
          <div className="border-b bg-background px-4 lg:px-6 py-4 min-w-0 w-full">
            <div className="min-w-0 w-full">
              <DashboardHeader 
                onNewDecision={navigateToNewDecision}
                onSignOut={handleSignOut}
                currentPage={currentPage}
                onSettings={navigateToSettings}
                onHelp={navigateToHelp}
                onUpgrade={navigateToPricing}
              />
            </div>
          </div>
          
          {/* Main content */}
          <main className="flex-1 overflow-auto p-4 lg:p-0">
            {currentPage === "dashboard" && (
              <DashboardContent 
                decisions={decisions}
                onUpgrade={navigateToPricing}
                onViewDecision={navigateToDecisionDetail}
                onNavigateToMyDecisions={navigateToMyDecisions}
                onNavigateToMyDecisionsWithTag={(tag) => {
                  setSelectedDecisionTag(tag);
                  setCurrentPage("my-decisions");
                }}
                onNavigateToReviews={navigateToReviews}
                onNavigateToAnalytics={navigateToAnalytics}
                onNavigateToInsights={navigateToAnalyticsInsights}
              />
            )}
            
            {currentPage === "new-decision" && (
              <NewDecisionForm 
                onSave={navigateToDashboard}
                onCancel={navigateToDashboard}
                onUpgrade={navigateToPricing}
              />
            )}
            
            {currentPage === "my-decisions" && (
              <MyDecisions 
                onViewDecision={navigateToDecisionDetail}
                onEditDecision={navigateToDecisionDetail}
                onNewDecision={navigateToNewDecision}
                {...(selectedDecisionTag && { initialTagFilter: selectedDecisionTag })}
              />
            )}
            
            {currentPage === "decision-detail" && selectedDecisionId && (
              selectedDecision ? (
                <div className="animate-fade-in">
                  <DecisionDetail 
                    key={selectedDecision.id}
                    decision={selectedDecision}
                    onBack={navigateToMyDecisions}
                  />
                </div>
              ) : decisions.length === 0 ? (
                <div className="p-6 text-center animate-fade-in">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading decision...</p>
                </div>
              ) : (
                <div className="p-6 text-center animate-fade-in">
                  <p className="text-muted-foreground">Decision not found</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Decision ID: {selectedDecisionId}
                  </p>
                  <button onClick={navigateToMyDecisions} className="mt-2 text-primary hover:underline">
                    Back to Decisions
                  </button>
                </div>
              )
            )}
            
            {currentPage === "analytics" && (
              <Analytics 
                decisions={decisions}
                defaultTab={analyticsDefaultTab}
              />
            )}
            
            {currentPage === "reviews" && (
              <Reviews 
                decisions={decisions}
                onViewDecision={navigateToDecisionDetail}
                onUpdateDecision={(decision: Decision) => {
                  // This functionality isn't implemented yet
                  console.log('Update decision:', decision);
                }}
                onAddReviewRating={(decisionId: string, reviewDay: number, rating: number, notes: string, outcome: string, lessons: string) =>
                  addRating(decisionId, reviewDay, rating, notes, outcome, lessons)
                }
                getCurrentRating={getCurrentRating}
              />
            )}
            
            {currentPage === "log" && (
              <Log 
                onNewDecision={navigateToNewDecision}
                onUpgrade={navigateToPricing}
              />
            )}
            
            {currentPage === "settings" && <Settings onUpgrade={navigateToPricing} />}
            {currentPage === "help" && <Help />}
            {currentPage === "pricing" && <Pricing onSignUp={navigateToSignUp} />}
            {currentPage === "app-blog" && <AppBlog onBack={navigateToDashboard} />}
            {currentPage === "app-best-practices" && <AppBestPractices onBack={navigateToDashboard} />}
          </main>
        </SidebarInset>
      </div>
      <ReleaseNotesPopup
        isOpen={showReleaseNotes}
        onClose={() => setShowReleaseNotes(false)}
      />
      <CommandPalette
        isAuthenticated={isAuthenticated}
        currentPage={currentPage}
        onNavigate={{
          navigateToDashboard,
          navigateToNewDecision,
          navigateToMyDecisions,
          navigateToAnalytics,
          navigateToReviews,
          navigateToLog,
          navigateToSettings,
          navigateToHelp,
          navigateToPricing,
          navigateToAppBlog,
          navigateToAppBestPractices,
          handleSignOut,
        }}
      />
    </SidebarProvider>
  );
}

/**
 * Sonner's toast outlet.
 *
 * Mounted at the root rather than inside AppContent's authenticated shell.
 * AppContent returns early for the landing, sign-in, sign-up and footer pages,
 * so an outlet rendered down there does not exist on any of them — a
 * `toast.error` from the landing page's pricing section, or from a checkout
 * that failed before the redirect, had nowhere to render and the click looked
 * like it did nothing.
 */
function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} richColors />;
}

export default function App() {
  return (
    <ToastProvider>
      <ThemeProvider defaultTheme="light">
        <AuthProvider>
          <ProfileProvider>
            <DecisionsProvider>
              <ThemedToaster />
              <PaymentHandler />
              <AppContent />
            </DecisionsProvider>
          </ProfileProvider>
        </AuthProvider>
      </ThemeProvider>
    </ToastProvider>
  );
}
